import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nsOptions } from '../i18n';
import TextUtil from '../utils/TextUtil';
import NewTooltip from './NewTooltip';
import SortUtil from '../utils/SortUtil';
import ElementUtil from '../utils/ElementUtil';
import {
  QUERY_STATUS_COLORS,
  QUERY_STATUS_NEW,
  QUERY_STATUS_COMMENTED,
  QUERY_STATUS_EDITED,
  QUERY_STATUS_RESOLVED,
  QUERY_STATUS_CLOSED,
  ELEMENT_TYPE_MODULE,
} from '../constants';

const NAME_MAX_LENGTH = 40;

const STATUS_TOOLTIP_KEYS = {
  [QUERY_STATUS_NEW]: 'inclusion:queries.status-new',
  [QUERY_STATUS_COMMENTED]: 'inclusion:queries.status-commented',
  [QUERY_STATUS_EDITED]: 'inclusion:queries.status-edited',
  [QUERY_STATUS_RESOLVED]: 'inclusion:queries.status-resolved',
  [QUERY_STATUS_CLOSED]: 'inclusion:queries.status-closed',
};

const mapStateToProps = (state) => ({
  projectPages: state.projectPages,
  projectElements: state.projectElements,
  elements: state.elements,
  projectEntries: state.projectEntries,
});

@connect(mapStateToProps)
@withTranslation('', nsOptions)
class QueryMonitoringTooltip extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    queries: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    projectPages: PropTypes.shape().isRequired,
    projectElements: PropTypes.shape().isRequired,
    elements: PropTypes.shape().isRequired,
    projectEntries: PropTypes.shape().isRequired,
    handleClick: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'open', // 'open' | 'closed'
    };
  }

  getQueriesByTab = () => {
    const { queries } = this.props;
    const { activeTab } = this.state;
    if (activeTab === 'open') {
      return queries.filter((q) => q.current_status !== QUERY_STATUS_CLOSED);
    }
    return queries.filter((q) => q.current_status === QUERY_STATUS_CLOSED);
  };

  /**
   * Group queries by page, then within each page separate standalone
   * variables from module variables (grouped by module PE then entry).
   *
   * Returns { pageId: { standalone: [...], modules: { modulePeId:
   *   { entries: { entryId: [...] } } } } }
   */
  getStructuredByPage = (filteredQueries) => {
    const { projectElements, elements } = this.props;
    const byPage = {};
    filteredQueries.forEach((query) => {
      const pe = projectElements[query.project_element];
      if (!pe) return;
      const pageId = pe.project_page;
      if (!byPage[pageId]) byPage[pageId] = { standalone: [], modules: {} };

      const element = elements[pe.element];
      if (pe.module && query.module) {
        // Variable inside an iterative form
        const modulePeId = pe.module;
        if (!byPage[pageId].modules[modulePeId]) {
          byPage[pageId].modules[modulePeId] = { entries: {}, sourceQuery: null };
        }
        const entryId = query.module;
        if (!byPage[pageId].modules[modulePeId].entries[entryId]) {
          byPage[pageId].modules[modulePeId].entries[entryId] = [];
        }
        byPage[pageId].modules[modulePeId].entries[entryId].push(
          { query, projectElement: pe },
        );
      } else if (element && element.type === ELEMENT_TYPE_MODULE) {
        // Query on the module source variable itself
        if (!byPage[pageId].modules[pe.id]) {
          byPage[pageId].modules[pe.id] = { entries: {}, sourceQuery: null };
        }
        byPage[pageId].modules[pe.id].sourceQuery = { query, projectElement: pe };
      } else {
        // Standalone variable
        byPage[pageId].standalone.push({ query, projectElement: pe });
      }
    });
    return byPage;
  };

  renderQueryItem = (query, pe) => {
    const { t, elements, handleClick } = this.props;
    const element = elements[pe.element];
    if (!element) return null;
    const varName = ElementUtil.formatElementName(element, t);
    const colors = QUERY_STATUS_COLORS[query.current_status] || {};
    const statusLabel = t(STATUS_TOOLTIP_KEYS[query.current_status] || '');

    return (
      <li key={`query-var-${query.id}`}>
        <NewTooltip
          content={(
            <div>
              <div>
                {t('inclusion:queries.tooltip-status')}
                {' : '}
                <strong>{statusLabel.toUpperCase()}</strong>
              </div>
              {query.current_comment && (
                <div>
                  {t('inclusion:queries.tooltip-comment')}
                  {' : '}
                  <strong>{query.current_comment}</strong>
                </div>
              )}
            </div>
          )}
        >
          <a
            role="button"
            tabIndex="-1"
            onClick={() => handleClick(pe.project_page, pe.id, pe.module, query.module)}
            onKeyPress={() => {}}
          >
            <FontAwesomeIcon
              icon={['fas', 'bell']}
              className="mr-2"
              style={{ color: colors.bg || '#999', fontSize: '0.8rem' }}
            />
            {varName}
          </a>
        </NewTooltip>
      </li>
    );
  };

  render() {
    const {
      t, title, queries, projectPages, projectElements, elements,
      projectEntries, handleClick,
    } = this.props;
    const { activeTab } = this.state;

    const openCount = queries.filter(
      (q) => q.current_status !== QUERY_STATUS_CLOSED,
    ).length;
    const closedCount = queries.filter(
      (q) => q.current_status === QUERY_STATUS_CLOSED,
    ).length;

    const filteredQueries = this.getQueriesByTab();
    const byPage = this.getStructuredByPage(filteredQueries);
    const pageIds = Object.keys(byPage).map(Number);
    const pages = Object.values(projectPages)
      .filter((p) => pageIds.includes(p.id))
      .sort(SortUtil.sortArray);

    return (
      <div>
        <div className="row mx-0 pb-2">
          <div className="col-12 px-1">
            <h5 className="text-center">{title}</h5>
          </div>
        </div>

        {/* Tabs */}
        <div className="row mb-3 mx-0">
          <div className="col px-1 text-center">
            <a
              className={`small ${activeTab === 'open' ? 'font-weight-bold' : ''}`}
              role="button"
              onClick={() => this.setState({ activeTab: 'open' })}
              onKeyPress={() => {}}
              tabIndex={0}
              style={{ textDecoration: activeTab === 'open' ? 'underline' : 'none' }}
            >
              {t('inclusion:queries.open-tab')}
              {` (${openCount})`}
            </a>
            <a
              className={`ml-4 small ${activeTab === 'closed' ? 'font-weight-bold' : ''}`}
              role="button"
              onClick={() => this.setState({ activeTab: 'closed' })}
              onKeyPress={() => {}}
              tabIndex={0}
              style={{ textDecoration: activeTab === 'closed' ? 'underline' : 'none' }}
            >
              {t('inclusion:queries.closed-tab')}
              {` (${closedCount})`}
            </a>
          </div>
        </div>

        {/* Content */}
        {filteredQueries.length > 0 ? (
          <>
            <div className="row mx-0">
              <div className="col px-1 pt-2">
                {pages.map((page) => {
                  const pageData = byPage[page.id];
                  const moduleIds = Object.keys(pageData.modules).map(Number);
                  return (
                    <span key={`query-page-${page.id}`}>
                      <NewTooltip
                        contentClassName="small"
                        content={page.title}
                        disabled={!(page.title.length > NAME_MAX_LENGTH)}
                      >
                        <h6 className="mb-2">
                          <a
                            role="button"
                            tabIndex="-1"
                            onClick={() => handleClick(page.id)}
                            onKeyPress={() => {}}
                            className=""
                          >
                            <strong>
                              {TextUtil.truncate(page.title, NAME_MAX_LENGTH)}
                            </strong>
                          </a>
                        </h6>
                      </NewTooltip>
                      <ul className="data-monitoring-list">
                        {/* Standalone (non-module) queries */}
                        {pageData.standalone.map(({ query, projectElement: pe }) => (
                          this.renderQueryItem(query, pe)
                        ))}
                        {/* Module queries — tree: module > entry > variables */}
                        {moduleIds.map((modulePeId) => {
                          const modulePe = projectElements[modulePeId];
                          if (!modulePe) return null;
                          const moduleElement = elements[modulePe.element];
                          if (!moduleElement) return null;
                          const moduleName = ElementUtil.formatElementName(
                            moduleElement, t,
                          );
                          const { entries, sourceQuery } = pageData.modules[
                            modulePeId];
                          const entryIds = Object.keys(entries).map(Number);
                          // Determine worst query status for the module bell
                          const allModuleQueries = [
                            ...(sourceQuery ? [sourceQuery.query] : []),
                            ...entryIds.flatMap(
                              (eid) => entries[eid].map((item) => item.query),
                            ),
                          ];
                          const moduleColor = (() => {
                            const statuses = allModuleQueries.map(
                              (q) => q.current_status,
                            );
                            if (statuses.includes(QUERY_STATUS_NEW)
                              || statuses.includes(QUERY_STATUS_EDITED)) {
                              return QUERY_STATUS_COLORS[QUERY_STATUS_NEW]?.bg
                                || '#EC5564';
                            }
                            if (statuses.includes(QUERY_STATUS_COMMENTED)
                              || statuses.includes(QUERY_STATUS_RESOLVED)) {
                              return QUERY_STATUS_COLORS[QUERY_STATUS_COMMENTED]?.bg
                                || '#FFC83D';
                            }
                            return QUERY_STATUS_COLORS[QUERY_STATUS_CLOSED]?.bg
                              || '#6d9942';
                          })();
                          return (
                            <li key={`query-module-${modulePeId}`}>
                              <a
                                role="button"
                                tabIndex="-1"
                                onClick={() => handleClick(page.id, null, modulePeId)}
                                onKeyPress={() => {}}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', 'bell']}
                                  className="mr-2"
                                  style={{
                                    color: moduleColor,
                                    fontSize: '0.8rem',
                                  }}
                                />
                                {moduleName}
                              </a>
                              <ul className="data-monitoring-list">
                                {entryIds.map((entryId) => {
                                  const entry = projectEntries[entryId];
                                  const entryLabel = entry
                                    ? TextUtil.truncate(
                                      entry.value || `#${entryId}`,
                                      NAME_MAX_LENGTH,
                                    ) : `#${entryId}`;
                                  return (
                                    <span key={`query-entry-${entryId}`}>
                                      <li>
                                        <a
                                          role="button"
                                          tabIndex="-1"
                                          onClick={() => handleClick(
                                            page.id, null, modulePeId, entryId,
                                          )}
                                          onKeyPress={() => {}}
                                        >
                                          {entryLabel}
                                        </a>
                                      </li>
                                      <ul className="data-monitoring-list">
                                        {entries[entryId].map(
                                          ({ query, projectElement: pe }) => (
                                            this.renderQueryItem(query, pe)
                                          ),
                                        )}
                                      </ul>
                                    </span>
                                  );
                                })}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </span>
                  );
                })}
              </div>
            </div>
            {/* Légende — uniquement sur l'onglet Ouvertes */}
            {activeTab === 'open' && (
            <div className="row mx-0 mt-3">
              <div className="col px-1 pt-2">
                <div className="d-flex align-items-center mb-1">
                  <FontAwesomeIcon
                    icon={['fas', 'bell']}
                    style={{ color: QUERY_STATUS_COLORS[QUERY_STATUS_NEW].bg, fontSize: '0.8rem' }}
                    className="mr-2"
                  />
                  <span className="small font-italic text-muted">
                    {t('inclusion:queries.legend-red')}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={['fas', 'bell']}
                    style={{ color: QUERY_STATUS_COLORS[QUERY_STATUS_COMMENTED].bg, fontSize: '0.8rem' }}
                    className="mr-2"
                  />
                  <span className="small font-italic text-muted">
                    {t('inclusion:queries.legend-orange')}
                  </span>
                </div>
              </div>
            </div>
            )}
          </>
        ) : (
          <div className="row mx-0">
            <div className="col px-1 text-center pt-3">
              <p>
                <strong>
                  {activeTab === 'open'
                    ? t('inclusion:queries.no-open-queries')
                    : t('inclusion:queries.no-closed-queries')}
                </strong>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default QueryMonitoringTooltip;
